/**
 * Single source of truth for the Order **Details** fields.
 *
 * Both the editable order form (`OrderDetailsForm`, app-client) and the
 * read-only Order details drawer (`OrderDetailsDrawer`) derive their field set
 * from this list, so the drawer can never miss a field the form shows nor add
 * one it doesn't. Dynamic "Company-specific info" fields are NOT listed here --
 * they come from the order's metadata at runtime.
 */
import type { IField } from '@moko/utils';

export type OrderDetailFieldKey =
  | 'tenant'
  | 'code'
  | 'description'
  | 'status'
  | 'orderForm'
  | 'orderEntity'
  | 'workType'
  | 'orderedForm'
  | 'pricingItems'
  | 'estimatedPrice'
  | 'actualPrice'
  | 'currencyCode';

export type OrderDetailGroup = 'basic' | 'pricingItems' | 'pricing';

export interface OrderDetailFieldDef {
  key: OrderDetailFieldKey;
  /** Form field name (the key in the order payload / EditItem values). */
  name: string;
  /** i18n key + fallback for the label. */
  labelKey: string;
  labelFallback: string;
  fieldType: 'String' | 'Lookup' | 'Number' | 'PricingItems';
  lookupList?: string;
  group: OrderDetailGroup;
  /** Occupies the full row inside a 2-column Basic/Pricing panel. */
  fullWidth?: boolean;
  /** Always read-only in FE (value comes from Order GET). */
  readonlyAlways?: boolean;
  /**
   * Hidden once the order exists (edit mode). Mirrors the form's
   * `effect: currentOrderId ? 'Hide' : 'Show'`. Such fields never appear in the
   * read-only drawer (which only ever shows existing orders) -- this is a
   * correctness rule the drawer MUST honour.
   */
  hiddenInEdit?: boolean;
  /**
   * Only rendered as an editable control when the user has more than one
   * tenant. This is a FORM-INPUT concern (don't show a tenant selector to
   * single-tenant users). The read-only drawer always shows the order's tenant
   * when present, since seeing it is informative -- so the drawer ignores this.
   */
  requiresMultiTenant?: boolean;
  /**
   * Hidden when the tenant cannot order assets (`feature-order-asset` off).
   */
  hiddenWhenForceWork?: boolean;
  multiSelect?: boolean;
}

export const ORDER_BASIC_PANEL = 'orderBasic';
export const ORDER_PRICING_ITEMS_PANEL = 'orderPricingItems';
export const ORDER_PRICING_PANEL = 'orderPricing';

/**
 * Declarative, ordered list of the Order Details base fields. Order here is the
 * render order in the Work Order tab and the read-only drawer.
 *
 * Basic 2-column pairing: Tenant|Code, Description (full), Status|Work form.
 * Pricing 2-column pairing: Estimated|Currency, Actual (full).
 */
export const ORDER_DETAIL_FIELDS: OrderDetailFieldDef[] = [
  {
    key: 'tenant',
    name: 'tenantId',
    labelKey: 'tenant',
    labelFallback: 'Tenant',
    fieldType: 'Lookup',
    lookupList: 'tenants',
    group: 'basic',
    requiresMultiTenant: true,
  },
  {
    key: 'code',
    name: 'code',
    labelKey: 'code',
    labelFallback: 'Code',
    fieldType: 'String',
    group: 'basic',
  },
  {
    key: 'description',
    name: 'description',
    labelKey: 'description',
    labelFallback: 'Description',
    fieldType: 'String',
    group: 'basic',
    fullWidth: true,
  },
  {
    key: 'status',
    name: 'statusId',
    labelKey: 'status',
    labelFallback: 'Status',
    fieldType: 'Lookup',
    lookupList: 'Statuses',
    group: 'basic',
  },
  {
    key: 'orderedForm',
    name: 'orderedMetadataName',
    labelKey: 'assetForm',
    labelFallback: 'Asset form',
    fieldType: 'Lookup',
    lookupList: 'orderedEntitiesMetadatas',
    group: 'basic',
  },
  {
    key: 'pricingItems',
    name: 'pricingItemIds',
    labelKey: 'pricingItems',
    labelFallback: 'Pricing items',
    fieldType: 'PricingItems',
    lookupList: 'pricingItems',
    group: 'pricingItems',
    fullWidth: true,
  },
  {
    key: 'orderForm',
    name: 'metadataName',
    labelKey: 'orderForm',
    labelFallback: 'Order form',
    fieldType: 'Lookup',
    lookupList: 'metadatas',
    group: 'basic',
    fullWidth: true,
    hiddenInEdit: true,
  },
  {
    key: 'orderEntity',
    name: 'orderedEntityType',
    labelKey: 'orderEntity',
    labelFallback: 'Order entity',
    fieldType: 'Lookup',
    lookupList: 'entityTypes',
    group: 'basic',
    fullWidth: true,
    hiddenWhenForceWork: true,
  },
  {
    key: 'workType',
    name: 'workTypeId',
    labelKey: 'workType',
    labelFallback: 'Work type',
    fieldType: 'Lookup',
    lookupList: 'workTypes',
    group: 'basic',
    fullWidth: true,
    readonlyAlways: true,
  },
  {
    key: 'estimatedPrice',
    name: 'estimatedPrice',
    labelKey: 'estimatedPrice',
    labelFallback: 'Estimated price',
    fieldType: 'Number',
    group: 'pricing',
    readonlyAlways: true,
  },
  {
    key: 'currencyCode',
    name: 'currencyCode',
    labelKey: 'currency',
    labelFallback: 'Currency',
    fieldType: 'String',
    group: 'pricing',
    readonlyAlways: true,
  },
  {
    key: 'actualPrice',
    name: 'actualPrice',
    labelKey: 'actualPrice',
    labelFallback: 'Actual price',
    fieldType: 'Number',
    group: 'pricing',
    fullWidth: true,
  },
];

export const BASIC_ORDER_FIELDS = ORDER_DETAIL_FIELDS.filter(
  (field) => field.group === 'basic'
);
export const PRICING_ITEM_FIELDS = ORDER_DETAIL_FIELDS.filter(
  (field) => field.group === 'pricingItems'
);
export const PRICING_ORDER_FIELDS = ORDER_DETAIL_FIELDS.filter(
  (field) => field.group === 'pricing'
);

/** The ordered-form field uses a Work/Asset-specific label. */
export const orderedFormLabel = (
  forceWorkEntity: boolean
): { key: string; fallback: string } =>
  forceWorkEntity
    ? { key: 'workForm', fallback: 'Work form' }
    : { key: 'assetForm', fallback: 'Asset form' };

/**
 * Resolves the label key/fallback for a field, accounting for the dynamic
 * Work/Asset-form label.
 */
export const orderFieldLabel = (
  field: OrderDetailFieldDef,
  forceWorkEntity: boolean
): { key: string; fallback: string } => {
  if (field.key === 'orderedForm') return orderedFormLabel(forceWorkEntity);
  return { key: field.labelKey, fallback: field.labelFallback };
};

const ORDER_FORM_VALUE_NAMES = new Set(
  ORDER_DETAIL_FIELDS.map((field) => field.name)
);

export const parseOrderOtherInfo = (
  value: unknown
): Record<string, unknown> => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return {};
    } catch {
      return {};
    }
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
};

export const pickOrderValue = (
  order: Record<string, unknown>,
  ...keys: string[]
): unknown => {
  for (const key of keys) {
    if (order[key] != null && order[key] !== '') return order[key];
  }
  return undefined;
};

export const toOptionalNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const flattenOrderFormValues = (
  order: object
): Record<string, unknown> => {
  const raw = order as Record<string, unknown>;
  const extra = parseOrderOtherInfo(raw.otherInfo);
  return {
    ...extra,
    tenantId: raw.tenantId != null ? String(raw.tenantId) : '',
    code: raw.code ?? '',
    description: raw.description ?? '',
    statusId: raw.statusId != null ? String(raw.statusId) : '',
    orderedEntityType: raw.orderedEntityType ?? '',
    orderedMetadataName: raw.orderedMetadataName ?? '',
    estimatedPrice:
      pickOrderValue(raw, 'estimatedPrice', 'EstimatedPrice') ?? '',
    actualPrice: pickOrderValue(raw, 'actualPrice', 'ActualPrice') ?? '',
    currencyCode: pickOrderValue(raw, 'currencyCode', 'CurrencyCode') ?? '',
    pricingItemIds: pricingItemIdsValue(raw.pricingItems),
    workTypeId:
      typeof raw.workTypeId === 'number' ? String(raw.workTypeId) : 'none',
  };
};

const pricingItemIdsValue = (value: unknown): string => {
  if (!Array.isArray(value)) return '';
  return value
    .map((item) => Number((item as { pricingItemId?: unknown }).pricingItemId))
    .filter((id) => Number.isFinite(id))
    .join('|');
};

export const splitOrderFormValues = (edited: Record<string, unknown>) => {
  const otherInfo: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(edited)) {
    if (!ORDER_FORM_VALUE_NAMES.has(key)) otherInfo[key] = value;
  }
  const parsedStatusId = edited.statusId
    ? Number.parseInt(String(edited.statusId), 10)
    : undefined;
  return {
    code: edited.code as string | undefined,
    description: edited.description as string | undefined,
    statusId: Number.isFinite(parsedStatusId) ? parsedStatusId : undefined,
    orderedEntityType: edited.orderedEntityType as string | undefined,
    orderedMetadataName: edited.orderedMetadataName as string | undefined,
    actualPrice: toOptionalNumber(edited.actualPrice),
    pricingItems: pricingItemsFromValue(edited.pricingItemIds),
    otherInfo,
  };
};

const pricingItemsFromValue = (value: unknown): { pricingItemId: number }[] => {
  const raw = Array.isArray(value)
    ? value.map(String)
    : String(value ?? '').split('|');
  return raw
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .map((pricingItemId) => ({ pricingItemId }));
};

export const filterOrderedMetadatas = (
  items: { key: string; value: string }[],
  entityType?: string | null
) => {
  if (!entityType) return [];
  const prefix = `${entityType}|`.toLowerCase();
  return items.filter((item) => item.key.toLowerCase().startsWith(prefix));
};

/** Return the field, or nothing to skip. */
export type MapOrderDetailField = (
  field: OrderDetailFieldDef,
  base: IField
) => IField | void;

const twoColGroup = (
  name: string,
  label: string,
  opts: { chrome: boolean; sectionTitle: boolean; headerTitle?: boolean }
): IField => ({
  name,
  fieldType: 'Panel',
  label,
  fullWidth: true,
  options: {
    columns: 2,
    chrome: opts.chrome,
    sectionTitle: opts.sectionTitle,
    ...(opts.headerTitle ? { headerTitle: true } : {}),
  },
});

const SUMMARY_BASIC_KEYS: OrderDetailFieldKey[] = [
  'description',
  'tenant',
  'status',
  'orderForm',
  'orderedForm',
  'code',
  'orderEntity',
];

/**
 * Builds Basic + Pricing 2-column groups from {@link ORDER_DETAIL_FIELDS}.
 *
 * Create-order form: `chrome: false` + `summaryLayout` (flat grid, no title).
 * Work Order tab: `chrome: false` + `sectionTitle` (Basic/Pricing labels, no panel).
 *
 * Company metadata is appended by the caller; those panels collapse only if
 * the form designer created them.
 */
export const buildGroupedOrderDetailFields = (
  t: (key: string, fallback: string) => string,
  forceWorkEntity: boolean,
  mapField: MapOrderDetailField,
  opts?: {
    includePricing?: boolean;
    chrome?: boolean;
    sectionTitle?: boolean;
    summaryLayout?: boolean;
  }
): IField[] => {
  const groupOpts = {
    chrome: opts?.chrome !== false,
    sectionTitle: opts?.sectionTitle === true,
  };
  const basicDefs = opts?.summaryLayout
    ? SUMMARY_BASIC_KEYS.flatMap((key) => {
        const field = BASIC_ORDER_FIELDS.find((item) => item.key === key);
        return field ? [{ ...field, fullWidth: false }] : [];
      })
    : BASIC_ORDER_FIELDS;

  const group = (defs: OrderDetailFieldDef[], parentName: string) =>
    defs.flatMap((field) => {
      const { key, fallback } = orderFieldLabel(field, forceWorkEntity);
      const mapped = mapField(field, {
        name: field.name,
        fieldType: field.fieldType,
        label: t(key, fallback),
        labelAbove: true,
        parentName,
        fullWidth: field.fullWidth,
        readonly: field.readonlyAlways,
        ...(field.lookupList ? { lookupList: field.lookupList } : {}),
        ...(field.multiSelect ? { multiSelect: true } : {}),
      });
      return mapped ? [mapped] : [];
    });

  const fields: IField[] = [
    twoColGroup(ORDER_BASIC_PANEL, t('basic', 'Basic'), groupOpts),
    ...group(basicDefs, ORDER_BASIC_PANEL),
    twoColGroup(ORDER_PRICING_ITEMS_PANEL, t('pricingItems', 'Pricing items'), {
      ...groupOpts,
      ...(groupOpts.sectionTitle ? {} : { headerTitle: true }),
    }),
    ...group(PRICING_ITEM_FIELDS, ORDER_PRICING_ITEMS_PANEL),
  ];
  if (opts?.includePricing !== false) {
    fields.push(
      twoColGroup(ORDER_PRICING_PANEL, t('pricing', 'Pricing'), groupOpts),
      ...group(PRICING_ORDER_FIELDS, ORDER_PRICING_PANEL)
    );
  }
  return fields;
};
